<template>
  <v-row class="py-4" no-gutters>
    <v-col cols="12" md="9" v-if="!isEventPassed">
      <v-alert
        class="suus__alert"
        border="top"
        color="primary"
        colored-border
        outlined
      >
        <v-row class="py-4">
          <v-col class="d-flex justify-sm-center" cols="12" sm="2">
            <StatusIcon v-if="alertContent.icon" :icon="alertContent.icon" />
          </v-col>
          <v-col cols="12" sm="9">
            <Header :text="alertContent.headerText" :level="5" />
            <div class="d-flex align-baseline pb-2">
              <TextBody class="pr-5" :text="alertContent.bodyText" />
              <TextButton
                :label="alertContent.registerBtn"
                plain
                color="primary"
                @click="toggleAttending"
              />
            </div>
            <div v-if="isRegistered" class="d-flex align-baseline">
              <TextBody
                :text="alertContent.guestText"
                customClass="mb-0 pr-5"
              />
              <div class="text-center">
                <v-dialog :width="modalWidth" v-model="showModal">
                  <template v-slot:activator="{ on, attrs }">
                    <TextButton
                      v-if="alertContent.guestBtn"
                      :label="alertContent.guestBtn"
                      plain
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <GuestModal
                    :guest_count="guestCount"
                    @close="showModal = false"
                  />
                </v-dialog>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-alert>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { EVENT_STATUS } from "constants/constants"
import { PropType } from "vue"
import StatusIcon from "components/UI/StatusIcon.vue"
import { mdiCheck, mdiClose } from "@mdi/js"
import Header from "components/UI/Header.vue"
import TextBody from "components/UI/TextBody.vue"
import TextButton from "components/UI/TextButton.vue"
import GuestModal from "components/Event/GuestModal.vue"
import useTranslate from "features/common/translate/concerns/useTranslate"

interface AlertContent {
  headerText: string
  bodyText: string
  icon: string
  registerBtn: string
  guestText: string
  guestBtn: string
}

export default defineComponent({
  name: "EventAttendAlert",
  components: { GuestModal, TextButton, TextBody, Header, StatusIcon },
  props: {
    attendStatus: {
      type: String as PropType<EVENT_STATUS>,
      required: true,
    },
    guestCount: {
      type: Number,
    },
    guestsAllowed: {
      type: Boolean,
    },
  },
  data: () => ({
    showModal: false,
    mdiCheck,
    mdiClose,
  }),
  computed: {
    modalWidth() {
      // TODO: Refactor into mixin, also used in AttendCard
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 600
        case "sm":
          return 800
        case "md":
          return 600
        default:
          return 800
      }
    },
    isEventPassed(): boolean {
      return this.attendStatus === EVENT_STATUS.EVENT_PASSED
    },
    register(): boolean {
      return this.attendStatus === EVENT_STATUS.REGISTER
    },
    isRegistered(): boolean {
      return (
        this.attendStatus === EVENT_STATUS.REGISTERED ||
        this.attendStatus === EVENT_STATUS.FINISHED_REGISTRATION ||
        this.attendStatus === EVENT_STATUS.ATTEND_ONLINE_NOW
      )
    },
    isDeregistered(): boolean {
      return this.attendStatus === EVENT_STATUS.DEREGISTERED
    },
    getEventStateTranslateComponent(): string {
      if (this.register) return "REGISTER"
      else if (this.isRegistered) return "REGISTERED"
      else if (this.isDeregistered) return "DEREGISTERED"
      return null
    },
    alertContent(): AlertContent {
      const eventState = this.getEventStateTranslateComponent
      if (!eventState) return null
      const { t: tEventState } = this.getComponent(eventState)
      return {
        headerText: tEventState("headerText"),
        bodyText: tEventState("bodyText"),
        icon: this.isRegistered
          ? mdiCheck
          : this.isDeregistered
          ? mdiClose
          : null,
        registerBtn: tEventState("registerBtn"),
        guestText: this.isRegistered ? this.hasGuests : null,
        guestBtn: this.isRegistered ? this.guestButtonText : null,
      }
    },
    hasGuests(): string {
      if (!this.guestsAllowed) return null
      const { t: tHasGuest } = useTranslate(
        "components.attendee_list.has_guests"
      )
      const guestQuantity =
        this.guestCount < 1 ? "none" : this.guestCount === 1 ? "one" : "more"
      return tHasGuest(guestQuantity, [this.guestCount])
    },
    guestButtonText(): string {
      if (!this.guestsAllowed) return null
      return "Ändern"
    },
  },
  methods: {
    toggleAttending() {
      const isAttend = !this.isRegistered
      this.$emit("toggleAttending", isAttend)
    },
  },
  setup() {
    const { t, getComponent } = useTranslate(
      "components.attendee_list.alert_content"
    )
    return {
      t,
      getComponent,
    }
  },
})
</script>

<style lang="sass">
$primary-color: rgba(69, 127, 252, 1)
.suus__alert.v-alert--outlined
  border: 1px solid $primary-color !important

  .v-alert__border--top
    border-top-left-radius: 2px
    border-top-right-radius: 2px
</style>
